import { copyOfferCollection, type Offer, type OfferCollection } from '$lib/model/offer';
import { businessStore } from '$lib/stores/business';
import type { Axios } from 'axios';
import { DateTime } from 'luxon';
import { get, writable } from 'svelte/store';
import { apiStore, deleteObject, saveObject } from './api';
import { t } from './i18n';
import { toast } from './toast';

const offerDefinition = () => {
	const { subscribe, set, update } = writable<Array<Offer>>([]);
	return {
		subscribe,
		set,
		update,
		getLogo: (offer: Offer) => {
			return offer.logo ? offer.logo : businessStore.getLogo();
		},
		reload: function () {
			(get(apiStore) as Axios).get<Offer[]>('/business/offer').then((res) => {
				this.refreshFromData(res.data);
			});
		},
		refreshFromData: function (data: Offer[] | undefined) {
			if (!data) this.set([]);
			else this.set(data.map((offer) => convertOfferApiToOffer(offer)));
		}
	};
};

export function convertOfferApiToOffer(offer: Offer): Offer {
	return {
		...offer,
		validityStart:
			typeof offer.validityStart === 'string' ? DateTime.fromISO(offer.validityStart) : null,
		validityEnd:
			typeof offer.validityEnd === 'string' ? DateTime.fromISO(offer.validityEnd) : null,
		publishStart:
			typeof offer.publishStart === 'string' ? DateTime.fromISO(offer.publishStart) : null,
		publishEnd: typeof offer.publishEnd === 'string' ? DateTime.fromISO(offer.publishEnd) : null
	};
}

export const offerStore = offerDefinition();
export const offerCollectionStore = writable<OfferCollection[]>([]);

export function loadOfferCollectionsFromData(data: OfferCollection[]) {
	offerCollectionStore.set(data);
}

let saveOrderTimeout: NodeJS.Timeout | undefined;
export function saveOfferOrder(offers: Offer[]) {
	clearTimeout(saveOrderTimeout);
	offerStore.set(get(offerStore));
	saveOrderTimeout = setTimeout(() => {
		const offerIds = get(offerCollectionStore)
			.map((c) =>
				offers
					.filter((o) => o.collectionId === c.id)
					.sort((a, b) => a.sortOrder - b.sortOrder)
					.map((o) => o.id)
			)
			.flat();

		get(apiStore)!
			.put('/business/offer/sort', offerIds)
			.catch((err) => {
				get(toast)({
					text: get(t)('server.error', { error: err.message }),
					bgColor: 'error'
				});
				console.error(err);
			});
	}, 1000);
}

let saveOfferCollectionOrderTimeout: NodeJS.Timeout | undefined;
export function saveOfferCollectionOrder() {
	clearTimeout(saveOfferCollectionOrderTimeout);
	offerCollectionStore.set(get(offerCollectionStore));
	saveOfferCollectionOrderTimeout = setTimeout(() => {
		const offerCollectionIds = get(offerCollectionStore)
			.sort((a, b) => a.sortOrder - b.sortOrder)
			.map((o) => o.id);
		get(apiStore)!
			.put('/business/offers/collection/sort', offerCollectionIds)
			.catch((err) => {
				get(toast)({
					text: get(t)('server.error', { error: err.message }),
					bgColor: 'error'
				});
				console.error(err);
			});
	}, 1000);
}

export function saveOfferCollection(offerCollection: OfferCollection) {
	return saveObject<OfferCollection>(
		'/business/offers/collection/' + offerCollection.id,
		offerCollection
	).then((res) => {
		offerCollectionStore.set(
			get(offerCollectionStore)
				.filter((c) => c.id !== offerCollection.id)
				.concat(copyOfferCollection(offerCollection))
		);
	});
}

export function deleteOfferCollection(offerCollection: OfferCollection) {
	return deleteObject('/business/offers/collection/' + offerCollection.id).then(() => {
		offerCollectionStore.set(
			get(offerCollectionStore).filter((collection) => collection.id !== offerCollection.id)
		);
	});
}
export function saveOffer(offer: Offer): Promise<Offer> {
	return saveObject<Offer>('/business/offer/' + offer.id, offer).then((res) => {
		offerStore.set(
			get(offerStore)
				.filter((o) => o.id != offer.id)
				.concat(
					JSON.parse(JSON.stringify(offer), (key, value) => {
						if (
							value &&
							(key === 'validityStart' ||
								key === 'validityEnd' ||
								key === 'publishStart' ||
								key === 'publishEnd')
						) {
							return DateTime.fromISO(value);
						}
						return value;
					})
				)
		);

		saveOfferGuestPrices(offer);

		return offer;
	});
}
export function deleteOffer(offer: Offer): Promise<void> {
	return deleteObject(`/business/offer/${offer.id}`)
		.then(() => {
			offerStore.set([
				...get(offerStore).map((o) => {
					if (o.id === offer.id) {
						o.archivedAt = DateTime.now();
					}
					return o;
				})
			]);
		})
		.catch((err) => {
			get(toast)({
				text: get(t)('server.error', { error: err.message }),
				bgColor: 'error'
			});
			console.error(err);
		});
}

export function saveOfferGuestPrices(offer: Offer): Promise<any> {
	return saveObject(`/business/offer/${offer.id}/prices`, {
		prices: offer.guestPrices
	});
}
